import axios from "axios";
import {
  getStoredToken,
  getStoredClientId,
  getStoredUser,
} from "../utils/tokenUtils";
import { injectClientIdIntoConfig } from "../utils/clientContext";
import { installApiLoadingInterceptors } from "../utils/apiLoading";
import {
  API_BASE_PREFIXED,
  addFallbackInterceptor,
} from "../config/api.config";

// Use prefixed URL (new standard)
const USER_SERVICE_URL = API_BASE_PREFIXED.user;

// Create axios instance for user service
const userServiceApi = axios.create({
  baseURL: USER_SERVICE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add fallback interceptor for backward compatibility
addFallbackInterceptor(
  userServiceApi,
  API_BASE_PREFIXED.user,
  process.env.REACT_APP_USER_SERVICE_URL || "http://localhost:3001",
);

// Add auth token interceptor
userServiceApi.interceptors.request.use(
  (config) => {
    const token = getStoredToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return injectClientIdIntoConfig(config);
  },
  (error) => Promise.reject(error),
);

installApiLoadingInterceptors(userServiceApi);

/**
 * RBAC Service for managing roles, permissions, and user assignments
 */
export const rbacService = {
  // ========== Permissions ==========
  /**
   * Get all permissions
   */
  getAllPermissions: async () => {
    const response = await userServiceApi.get("/api/v1/permissions");
    return response.data;
  },

  /**
   * Get permissions grouped by feature
   */
  getPermissionsGrouped: async () => {
    const response = await userServiceApi.get("/api/v1/permissions/grouped");
    return response.data;
  },

  /**
   * Get permissions by feature
   */
  getPermissionsByFeature: async (feature) => {
    const response = await userServiceApi.post("/api/v1/permissions/feature", {
      feature: feature,
    });
    return response.data;
  },

  // ========== Roles ==========
  /**
   * Get role by ID
   */
  getRole: async (roleId, clientId) => {
    const response = await userServiceApi.post("/api/v1/roles/get", {
      role_id: roleId,
      client_id: clientId,
    });
    return response.data;
  },

  /**
   * List roles
   */
  getRoles: async (filters = {}) => {
    const user = getStoredUser();
    const storedClientId = getStoredClientId();
    const clientId = filters.client_id || storedClientId || user?.client_id;

    const response = await userServiceApi.get("/api/v1/roles", {
      params: {
        client_id: clientId,
        ...filters,
      },
    });
    return response.data;
  },

  /**
   * Create role
   */
  createRole: async (roleData) => {
    const user = getStoredUser();
    const storedClientId = getStoredClientId();
    const payload = {
      ...roleData,
      client_id: roleData.client_id || storedClientId || user?.client_id,
    };
    const response = await userServiceApi.post("/api/v1/roles", payload);
    return response.data;
  },

  /**
   * Update role
   */
  updateRole: async (roleId, updates) => {
    const user = getStoredUser();
    const storedClientId = getStoredClientId();
    const payload = {
      role_id: roleId,
      ...updates,
      client_id: updates.client_id || storedClientId || user?.client_id,
    };
    const response = await userServiceApi.post("/api/v1/roles/update", payload);
    return response.data;
  },

  /**
   * Duplicate role
   */
  duplicateRole: async (roleId, newRoleName, clientId) => {
    const response = await userServiceApi.post("/api/v1/roles/duplicate", {
      role_id: roleId,
      new_role_name: newRoleName,
      client_id: clientId,
    });
    return response.data;
  },

  /**
   * Delete role
   */
  deleteRole: async (roleId, clientId) => {
    const response = await userServiceApi.post("/api/v1/roles/delete", {
      role_id: roleId,
      client_id: clientId,
    });
    return response.data;
  },

  // ========== Role Permissions ==========
  /**
   * Get role permissions
   */
  getRolePermissions: async (roleId, clientId) => {
    const response = await userServiceApi.post(
      "/api/v1/roles/permissions/get",
      {
        role_id: roleId,
        client_id: clientId,
      },
    );
    return response.data;
  },

  /**
   * Update role permissions
   */
  updateRolePermissions: async (roleId, permissions, clientId) => {
    const response = await userServiceApi.post(
      "/api/v1/roles/permissions/update",
      {
        role_id: roleId,
        permissions,
        client_id: clientId,
      },
    );
    return response.data;
  },

  /**
   * Get effective permissions (including inherited)
   */
  getEffectivePermissions: async (roleId, clientId) => {
    const response = await userServiceApi.post(
      "/api/v1/roles/permissions/effective",
      {
        role_id: roleId,
        client_id: clientId,
      },
    );
    return response.data;
  },

  // ========== CLIENT_CONFIGURATION ==========
  /**
   * Get CLIENT_CONFIGURATION permissions for a client
   */
  getClientConfigurationPermissions: async (clientId) => {
    const response = await userServiceApi.post(
      "/api/v1/roles/client-configuration/get",
      {
        client_id: clientId,
      },
    );
    return response.data;
  },

  /**
   * Update CLIENT_CONFIGURATION permissions for a client
   */
  updateClientConfigurationPermissions: async (clientId, permissions) => {
    const response = await userServiceApi.post(
      "/api/v1/roles/client-configuration/update",
      {
        client_id: clientId,
        permissions: permissions,
      },
    );
    return response.data;
  },

  // ========== User Role Assignments ==========
  /**
   * Get user roles
   */
  getUserRoles: async (userId, clientId) => {
    const response = await userServiceApi.post("/api/v1/users/roles/get", {
      user_id: userId,
      client_id: clientId,
    });
    return response.data;
  },

  /**
   * Assign role to user
   */
  assignRoleToUser: async (userId, roleId, clientId, projectId = null) => {
    const response = await userServiceApi.post("/api/v1/users/roles/assign", {
      user_id: userId,
      role_id: roleId,
      client_id: clientId,
      project_id: projectId,
    });
    return response.data;
  },

  /**
   * Remove role from user
   */
  removeRoleFromUser: async (userId, userRoleMapId, clientId) => {
    const response = await userServiceApi.post("/api/v1/users/roles/remove", {
      user_role_map_id: userRoleMapId,
      client_id: clientId,
    });
    return response.data;
  },

  // ========== Project Members ==========
  /**
   * Get project members
   */
  getProjectMembers: async (projectId, clientId) => {
    const response = await userServiceApi.post(
      "/api/v1/users/projects/members",
      {
        project_id: projectId,
        client_id: clientId,
      },
    );
    return response.data;
  },

  /**
   * Update project members (bulk)
   */
  updateProjectMembers: async (projectId, members, clientId) => {
    const response = await userServiceApi.put(
      "/api/v1/users/projects/members",
      {
        project_id: projectId,
        members,
        client_id: clientId,
      },
    );
    return response.data;
  },

  // ========== Audit Logs ==========
  /**
   * Get audit logs
   */
  getAuditLogs: async (filters = {}) => {
    const user = getStoredUser();
    const storedClientId = getStoredClientId();
    const clientId = filters.client_id || storedClientId || user?.client_id;

    const response = await userServiceApi.get("/api/v1/audit", {
      params: {
        client_id: clientId,
        ...filters,
      },
    });
    return response.data;
  },

  /**
   * Export audit logs
   */
  exportAuditLogs: async (filters = {}) => {
    const user = getStoredUser();
    const storedClientId = getStoredClientId();
    const clientId = filters.client_id || storedClientId || user?.client_id;

    const response = await userServiceApi.get("/api/v1/audit/export", {
      params: {
        client_id: clientId,
        ...filters,
      },
      responseType: "blob",
    });
    return response.data;
  },
};

export default rbacService;
